import React, { useEffect, useState } from 'react';
import { telemetryApi, vdaqApi } from '../../services/api';
import api from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { usePlan } from '../../hooks/usePlan';
import { useNavigate } from 'react-router-dom';
import { Card, CardStack, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Satellite, Copy, RefreshCcw, MapPin, Activity, Loader2, CheckCircle2, Radio, Trash2, Lock, Sparkles, Wifi, WifiOff, AlertTriangle, Clock, BellOff, ExternalLink } from 'lucide-react';
import AssetMapDrawer from '../AssetMapDrawer';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const PROVIDERS = [
  'Teletrac Navman',
  'Geotab',
  'Verizon Connect',
  'Procon',
  'Samsara',
  'Trimble',
  'Fleet Complete',
  'EziWay (TCP / Teltonika)',
  'V-DAQ (live telemetry)',
  'Other / Custom',
];

const GATEWAY_ADDRESS =
  process.env.REACT_APP_TELEMATICS_GATEWAY_ADDRESS || 'gps.equiprsoftware.com:5027';

const TelemetryCard = ({ assetId }) => {
  const { formatDateTime } = useSettings();
  const { isPro, loading: planLoading } = usePlan();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [config, setConfig] = useState({
    enabled: false,
    provider_name: '',
    auto_update_hours: true,
    auto_update_odometer: true,
  });
  const [token, setToken] = useState(null);
  const [imei, setImei] = useState(null);
  const [imeiDraft, setImeiDraft] = useState('');
  const [imeiSaving, setImeiSaving] = useState(false);
  const [lastPosition, setLastPosition] = useState(null);
  const [readings, setReadings] = useState([]);
  const [imeiStatus, setImeiStatus] = useState(null);
  const [silenceMuted, setSilenceMuted] = useState(false);
  const [muteSaving, setMuteSaving] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [vdaqVehicles, setVdaqVehicles] = useState([]);
  const [vdaqLinkedVid, setVdaqLinkedVid] = useState('');
  const [vdaqBusy, setVdaqBusy] = useState(false);

  const loadVdaqVehicles = async () => {
    try {
      const r = await vdaqApi.vehicles();
      const rows = r.data || [];
      setVdaqVehicles(rows);
      const mine = rows.find((v) => v.linked_asset_id === assetId);
      setVdaqLinkedVid(mine ? mine.vehicle_id : '');
    } catch (e) {
      setVdaqVehicles([]);
    }
  };

  const handleVdaqPick = async (vehicleId) => {
    setVdaqBusy(true);
    try {
      if (!vehicleId) {
        await vdaqApi.unlink(assetId);
        setVdaqLinkedVid('');
        toast.success('Unlinked from V-DAQ');
      } else {
        const v = vdaqVehicles.find((x) => x.vehicle_id === vehicleId);
        await vdaqApi.link({ vehicle_id: vehicleId, asset_id: assetId, registration: v?.registration, vin: v?.vin });
        setVdaqLinkedVid(vehicleId);
        toast.success(`Linked to ${v?.registration || vehicleId} — live data will sync shortly`);
      }
      loadVdaqVehicles();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Could not update V-DAQ link');
    } finally {
      setVdaqBusy(false);
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const webhookUrl = token ? `${backendUrl}/api/telemetry/ingest/${token}` : null;

  const load = async () => {
    try {
      const res = await telemetryApi.getConfig(assetId);
      const c = res.data.config || {};
      setConfig({
        enabled: !!c.enabled,
        provider_name: c.provider_name === 'V-DAQ' ? 'V-DAQ (live telemetry)' : (c.provider_name || ''),
        auto_update_hours: c.auto_update_hours !== false,
        auto_update_odometer: c.auto_update_odometer !== false,
      });
      setToken(res.data.telemetry_token || null);
      setImei(res.data.telemetry_imei || null);
      setImeiDraft(res.data.telemetry_imei || '');
      setLastPosition(res.data.last_position || null);
      setSilenceMuted(!!res.data.silence_alerts_muted);
    } catch (e) {
      // 404 just means never configured — that's OK
    } finally {
      setLoading(false);
    }
  };

  const loadReadings = async () => {
    try {
      const res = await telemetryApi.getReadings(assetId, 10);
      setReadings(res.data || []);
    } catch (e) {
      setReadings([]);
    }
  };

  const loadImeiStatus = async (i) => {
    const target = (i || imei || '').trim();
    if (!target) {
      setImeiStatus(null);
      return;
    }
    try {
      const res = await telemetryApi.imeiStatus(target);
      setImeiStatus(res.data || null);
    } catch (e) {
      setImeiStatus(null);
    }
  };

  // "Refresh" pulls BOTH the latest position (top stats) and the readings
  // table. Without re-running load() the Live Status header stays stale until
  // the drawer is reopened — which was the bug the user hit.
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([load(), loadReadings(), loadImeiStatus()]);
      toast.success('Live status refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, [assetId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api
      .get('/tenant-settings/telematics/usage')
      .then((r) => setUsage(r.data))
      .catch(() => setUsage(null));
  }, [config.enabled, imei]); // refresh on toggles + IMEI changes

  useEffect(() => {
    if (config.enabled && token) loadReadings();
  }, [config.enabled, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pull per-IMEI live status whenever the registered IMEI changes. Auto-poll
  // every 30s while the drawer is open so users don't have to keep hitting
  // Refresh to watch a device come online during install/commissioning.
  useEffect(() => {
    if (!imei) {
      setImeiStatus(null);
      return undefined;
    }
    loadImeiStatus(imei);
    const t = setInterval(() => loadImeiStatus(imei), 30000);
    return () => clearInterval(t);
  }, [imei]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (config.enabled && config.provider_name === 'V-DAQ (live telemetry)') loadVdaqVehicles();
  }, [config.enabled, config.provider_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await telemetryApi.updateConfig(assetId, config);
      setToken(res.data.telemetry_token || token);
      toast.success(config.enabled ? 'Telemetry enabled' : 'Telemetry saved');
      if (config.enabled) loadReadings();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && typeof detail === 'object') {
        toast.error(detail.message || 'Upgrade required');
        navigate(detail.upgrade_url || '/settings/billing');
      } else {
        toast.error((typeof detail === 'string' ? detail : null) || 'Failed to save telemetry config');
      }
    } finally {
      setSaving(false);
    }
  };

  const [rotating, setRotating] = useState(false);
  const handleRotate = async () => {
    if (!window.confirm('Issue a new webhook token? The current URL stops working straight away. Update the device or provider with the new URL.')) return;
    setRotating(true);
    try {
      const res = await telemetryApi.rotateToken(assetId);
      setToken(res.data.telemetry_token);
      toast.success('New webhook token issued');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rotate token');
    } finally {
      setRotating(false);
    }
  };

  const copyWebhook = async () => {
    if (!webhookUrl) return;
    await navigator.clipboard.writeText(webhookUrl);
    toast.success('Webhook URL copied');
  };

  const copyGatewayAddress = async () => {
    await navigator.clipboard.writeText(GATEWAY_ADDRESS);
    toast.success('Gateway address copied');
  };

  const handleRegisterImei = async () => {
    const trimmed = (imeiDraft || '').trim();
    if (!/^\d{14,17}$/.test(trimmed)) {
      toast.error('IMEI must be 14-17 digits');
      return;
    }
    setImeiSaving(true);
    try {
      await telemetryApi.registerImei(assetId, trimmed);
      setImei(trimmed);
      toast.success('IMEI registered — the gateway will accept this device on its next connection');
      loadImeiStatus(trimmed);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && typeof detail === 'object') {
        toast.error(detail.message || 'Upgrade required');
        navigate(detail.upgrade_url || '/settings/billing');
      } else {
        toast.error((typeof detail === 'string' ? detail : null) || 'Failed to register IMEI');
      }
    } finally {
      setImeiSaving(false);
    }
  };

  const handleUnregisterImei = async () => {
    setImeiSaving(true);
    try {
      await telemetryApi.unregisterImei(assetId);
      setImei(null);
      setImeiDraft('');
      toast.success('IMEI unregistered');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unregister IMEI');
    } finally {
      setImeiSaving(false);
    }
  };

  const handleToggleMute = async (next) => {
    setMuteSaving(true);
    const prev = silenceMuted;
    setSilenceMuted(next); // optimistic
    try {
      await telemetryApi.setSilenceMute(assetId, next);
      toast.success(next ? 'Silence alerts muted for this asset' : 'Silence alerts re-enabled');
    } catch (e) {
      setSilenceMuted(prev);
      toast.error(e.response?.data?.detail || 'Failed to update mute setting');
    } finally {
      setMuteSaving(false);
    }
  };

  const lastUpdatedAt = lastPosition?.updated_at
    ? formatDateTime(lastPosition.updated_at)
    : null;
  const minutesSince = lastPosition?.updated_at
    ? Math.floor((Date.now() - new Date(lastPosition.updated_at).getTime()) / 60000)
    : null;
  const stale = minutesSince !== null && minutesSince > 60;

  if (loading || planLoading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // ── FREE PLAN: visible but locked ────────────────────────────────────────
  if (!isPro && !config.enabled) {
    return (
      <Card data-testid="telemetry-card-locked">
        <CardHeader className="pb-3">
          <CardTitle className="as-section">
            <Satellite className="w-4 h-4 text-[#C9A400]" />
            GPS &amp; Telemetry
            <Badge variant="outline" className="lm-warn ml-auto text-[10px]">
              <Lock className="w-2.5 h-2.5 mr-1" />
              Pro feature
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Telematics is part of the <strong className="text-foreground">Pro plan</strong>. Wire up
            EziWay, Geotab, Samsara, Teletrac Navman, or any TCP/JSON GPS provider — the first 10
            devices are included, additional devices are <strong className="text-foreground">$1.99 per device per month</strong>.
          </p>
          <ul className="text-xs space-y-1.5 text-muted-foreground">
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-[#047857]" />Real-time position, speed, ignition + driver behaviour</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-[#047857]" />Auto-update engine hours &amp; odometer on every asset</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-[#047857]" />Trigger usage-based maintenance from GPS data</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-[#047857]" />Raw-TCP gateway included — paste an IMEI, you're live</li>
          </ul>
          <Button
            className="btn-volt"
            onClick={() => navigate('/settings/billing')}
            data-testid="telemetry-upgrade-cta"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade to Pro to enable
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <CardStack data-testid="telemetry-card">
      {/* Master toggle + status */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="as-section">
              <Satellite className="w-4 h-4 text-[#C9A400]" />
              GPS &amp; Telemetry
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {config.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch
                checked={config.enabled}
                onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
                data-testid="telemetry-enabled-toggle"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* Pro usage strip — surfaces device count + projected charge in-context */}
          {isPro && usage && (
            <div
              className={`rounded-lg border px-3 py-2.5 ${
                usage.devices_paid > 0
                  ? 'lm-strip-volt'
                  : usage.devices_active / Math.max(1, usage.devices_included) >= 0.8
                  ? 'lm-warn'
                  : 'lm-strip-neutral'
              }`}
              data-testid="telemetry-usage-strip"
            >
              <div className="flex items-center justify-between gap-4 flex-wrap text-xs">
                <span className="font-mono">
                  <span className="text-foreground font-semibold">{usage.devices_active}</span>
                  <span className="text-muted-foreground">
                    {' '}
                    {usage.devices_active === 1 ? 'GPS device' : 'GPS devices'}
                    {usage.devices_included > 0 && (
                      <> · {usage.devices_included} included free</>
                    )}
                  </span>
                  {usage.devices_paid > 0 && (
                    <span className="text-[#8A6D00] font-semibold ml-1.5">
                      · {usage.devices_paid} × {usage.per_device_price_display}/mo
                    </span>
                  )}
                </span>
                <span className="text-muted-foreground">
                  Projected:{' '}
                  <strong
                    className={usage.devices_paid > 0 ? 'text-[#8A6D00] font-mono' : 'text-foreground font-mono'}
                    data-testid="telemetry-card-projected"
                  >
                    {usage.estimated_monthly_charge_display}
                  </strong>
                </span>
              </div>
            </div>
          )}

          {/* Provider + auto-update toggles (only when enabled) */}
          {config.enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Provider
                  </Label>
                  <Select
                    value={config.provider_name || 'unset'}
                    onValueChange={(v) =>
                      setConfig({ ...config, provider_name: v === 'unset' ? '' : v })
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="telemetry-provider">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">— Select provider —</SelectItem>
                      {PROVIDERS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {config.provider_name === 'V-DAQ (live telemetry)' && (
                    <div className="mt-2" data-testid="telemetry-vdaq-picker">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                        V-DAQ vehicle
                      </Label>
                      <select
                        value={vdaqLinkedVid}
                        disabled={vdaqBusy}
                        onChange={(e) => handleVdaqPick(e.target.value)}
                        className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                        data-testid="telemetry-vdaq-vehicle"
                      >
                        <option value="">— Not linked —</option>
                        {vdaqVehicles.map((v) => (
                          <option key={v.vehicle_id} value={v.vehicle_id}
                            disabled={v.linked_asset_id && v.linked_asset_id !== assetId}>
                            {v.registration || '(no reg)'}{v.vin ? ` · ${v.vin}` : ''}
                            {v.linked_asset_id && v.linked_asset_id !== assetId ? ` (linked: ${v.linked_asset_label})` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1.5 text-[11px] text-muted-foreground">
                        Linking pulls live location &amp; engine hours from V-DAQ and tags this asset "via V-DAQ".
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Auto-update engine hours
                    </Label>
                    <Switch
                      checked={config.auto_update_hours}
                      onCheckedChange={(v) => setConfig({ ...config, auto_update_hours: v })}
                      data-testid="telemetry-auto-hours"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Auto-update odometer
                    </Label>
                    <Switch
                      checked={config.auto_update_odometer}
                      onCheckedChange={(v) => setConfig({ ...config, auto_update_odometer: v })}
                      data-testid="telemetry-auto-odo"
                    />
                  </div>
                </div>
              </div>

              {/* Webhook URL */}
              {webhookUrl && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Webhook URL — paste into your GPS provider
                  </Label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      readOnly
                      value={webhookUrl}
                      className="font-mono text-xs"
                      data-testid="telemetry-webhook-url"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={copyWebhook}
                      data-testid="telemetry-copy-webhook"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRotate}
                      disabled={rotating}
                      data-testid="telemetry-rotate-token"
                    >
                      {rotating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                      New token
                    </Button>
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    POST JSON with at minimum <code className="font-mono">{`{ latitude, longitude }`}</code>.
                    Optional: <code className="font-mono">speed_kmh, odometer_km, engine_hours, ignition, heading, fuel_level, timestamp</code>.
                  </p>
                </div>
              )}
            </>
          )}

          {!config.enabled && (
            <p className="text-sm text-muted-foreground">
              Connect this asset to any GPS provider (Teletrac Navman, Geotab, Procon, Samsara…).
              Toggle on to generate a unique webhook URL.
            </p>
          )}

          <div className="flex items-center justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="btn-volt"
              data-testid="telemetry-save"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save telemetry settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Direct TCP Gateway (for binary AVL devices: EziWay, Teltonika FMB/FMC, …) */}
      {config.enabled && token && (
        <Card data-testid="telemetry-gateway-card">
          <CardHeader className="pb-3">
            <CardTitle className="as-section">
              <Radio className="w-4 h-4 text-[#C9A400]" />
              Direct TCP gateway — binary AVL devices
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Use this if your GPS provider only supports raw TCP (Teltonika Codec 8, EziWay, etc.) and can't
              POST JSON to the webhook above. Hand your provider the gateway address below, paste the device's
              IMEI here, and the EQUIPR Gateway will translate inbound packets into telemetry for this asset.
            </p>

            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Gateway address — provide this to your GPS provider
              </Label>
              <div className="mt-1 flex gap-2">
                <Input
                  readOnly
                  value={GATEWAY_ADDRESS}
                  className="font-mono text-xs"
                  data-testid="telemetry-gateway-address"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyGatewayAddress}
                  data-testid="telemetry-copy-gateway"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Protocol: Teltonika Codec 8 / 8E (TCP, IMEI handshake). TLS variant on port 5028 coming Phase B.
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Device IMEI (15 digits)
              </Label>
              <div className="mt-1 flex gap-2">
                <Input
                  value={imeiDraft}
                  onChange={(e) => setImeiDraft(e.target.value.replace(/\D/g, '').slice(0, 17))}
                  placeholder="e.g. 350612345678901"
                  inputMode="numeric"
                  className="font-mono text-xs"
                  data-testid="telemetry-imei-input"
                />
                {imei && imei === imeiDraft.trim() ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleUnregisterImei}
                    disabled={imeiSaving}
                    className="lm-del-btn"
                    data-testid="telemetry-imei-unregister"
                  >
                    {imeiSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Unregister
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleRegisterImei}
                    disabled={imeiSaving || !imeiDraft.trim()}
                    className="btn-volt"
                    data-testid="telemetry-imei-register"
                  >
                    {imeiSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    {imei ? 'Update' : 'Register'}
                  </Button>
                )}
              </div>
              {imei ? (
                <>
                  <DeviceStatusPanel
                    imei={imei}
                    status={imeiStatus}
                    formatDateTime={formatDateTime}
                  />
                  <div className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-[#E2DED4] bg-[#FBFAF7] px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5">
                        <BellOff className="w-3.5 h-3.5" />
                        Mute silence alerts
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground leading-relaxed">
                        Skip the 24h-silent notification for this asset only — useful for units in
                        seasonal storage or off-hire. Fleet-wide alerts remain on.
                      </p>
                    </div>
                    <Switch
                      checked={silenceMuted}
                      onCheckedChange={handleToggleMute}
                      disabled={muteSaving}
                      data-testid="telemetry-mute-silence"
                    />
                  </div>
                </>
              ) : (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Find the IMEI on the GPS device label or in the provider's portal. 14-17 digits, numbers only.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Last-known position + recent readings */}
      {config.enabled && token && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="as-section">
                <Activity className="w-4 h-4 text-[#C9A400]" />
                Live status
              </CardTitle>
              <div className="flex items-center gap-1">
                {lastPosition?.latitude != null && lastPosition?.longitude != null && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setMapOpen(true)}
                      data-testid="telemetry-open-map"
                      title="View on interactive map"
                    >
                      <MapPin className="w-3.5 h-3.5 mr-2" />
                      Map
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const url = `https://www.google.com/maps?q=${lastPosition.latitude},${lastPosition.longitude}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                      data-testid="telemetry-view-on-map"
                      title="Open in Google Maps (native nav)"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-2" />
                      Open in Google
                    </Button>
                  </>
                )}
                <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="telemetry-refresh">
                  <RefreshCcw className={`w-3.5 h-3.5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing…' : 'Refresh'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {lastPosition ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Last seen" value={lastUpdatedAt || '—'} sub={stale ? 'Stale (> 1h)' : 'Active'} tone={stale ? 'warn' : 'ok'} />
                <Stat
                  label="Position"
                  value={
                    lastPosition.latitude && lastPosition.longitude
                      ? `${Number(lastPosition.latitude).toFixed(4)}, ${Number(lastPosition.longitude).toFixed(4)}`
                      : '—'
                  }
                  icon={MapPin}
                />
                <Stat label="Speed" value={lastPosition.speed_kmh != null ? `${lastPosition.speed_kmh} km/h` : '—'} />
                <Stat label="Ignition" value={lastPosition.ignition === true ? 'ON' : lastPosition.ignition === false ? 'OFF' : '—'} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No readings yet. Once your GPS provider starts pushing to the webhook URL above, live data will appear here.
              </p>
            )}

            {readings.length > 0 && (
              <div className="mt-6">
                <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                  Recent readings
                </h4>
                <div className="rounded-sm border border-border overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/30">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-medium">Time</th>
                        <th className="px-3 py-2 font-medium">Lat / Lng</th>
                        <th className="px-3 py-2 font-medium">Speed</th>
                        <th className="px-3 py-2 font-medium">Hours</th>
                        <th className="px-3 py-2 font-medium">Odometer</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border font-mono">
                      {readings.slice(0, 10).map((r) => (
                        <tr key={r.id}>
                          <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(r.timestamp)}</td>
                          <td className="px-3 py-2">
                            {r.latitude != null && r.longitude != null
                              ? `${Number(r.latitude).toFixed(4)}, ${Number(r.longitude).toFixed(4)}`
                              : '—'}
                          </td>
                          <td className="px-3 py-2">{r.speed_kmh != null ? `${r.speed_kmh} km/h` : '—'}</td>
                          <td className="px-3 py-2">{r.engine_hours != null ? `${r.engine_hours} h` : '—'}</td>
                          <td className="px-3 py-2">{r.odometer_km != null ? `${r.odometer_km} km` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <AssetMapDrawer
        open={mapOpen}
        onOpenChange={setMapOpen}
        assetId={assetId}
        assetLabel={imei ? `IMEI ${imei}` : 'Asset'}
        lastPosition={lastPosition}
      />
    </CardStack>
  );
};

// Per-IMEI device status panel — surfaces gateway connectivity, last packet
// time and 24h packet count so installers can confirm a device is talking to
// the gateway without needing SSH access. Auto-refreshes via parent's 30s
// polling effect.
const DEVICE_STATUS_META = {
  connected:    { label: 'Connected',       tone: 'ok',    icon: Wifi,          help: 'Device has an open TCP session on the gateway.' },
  recent:       { label: 'Recent packets',  tone: 'ok',    icon: Activity,      help: 'Packets received within the last hour. Device is talking but not in an active session right now.' },
  stale:        { label: 'Stale',           tone: 'warn',  icon: Clock,         help: 'No packets in the last hour. Check device power / SIM / ignition.' },
  never_seen:   { label: 'Never connected', tone: 'warn',  icon: WifiOff,       help: 'IMEI is registered but the gateway has never received a packet. Confirm device APN, server config (gps.equiprsoftware.com:5027) and that ignition is ON.' },
  gateway_down: { label: 'Gateway offline', tone: 'crit',  icon: AlertTriangle, help: 'The EQUIPR gateway itself is offline — no device can connect right now. Contact support.' },
};

const formatAgo = (secs) => {
  if (secs == null) return null;
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const DeviceStatusPanel = ({ imei, status, formatDateTime }) => {
  const meta = DEVICE_STATUS_META[status?.device_status] || {
    label: 'Loading…',
    tone: 'muted',
    icon: Loader2,
    help: 'Checking gateway for this device…',
  };
  const Icon = meta.icon;
  const toneClasses =
    meta.tone === 'ok'
      ? 'lm-ok'
      : meta.tone === 'warn'
      ? 'lm-dev-warn'
      : meta.tone === 'crit'
      ? 'lm-crit'
      : 'lm-strip-neutral text-[#59554D]';

  const lastPacketAgo = formatAgo(status?.seconds_since_last_packet);
  const hbAgo = formatAgo(status?.gateway?.seconds_since_heartbeat);

  return (
    <div className={`mt-3 rounded-lg border px-3 py-2.5 ${toneClasses}`} data-testid="telemetry-imei-status">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
        <Icon className={`w-3.5 h-3.5 ${status?.device_status ? '' : 'animate-spin'}`} />
        <span>{meta.label}</span>
        <span className="ml-auto font-mono text-[10.5px] opacity-70">IMEI · {imei}</span>
      </div>
      <p className="mt-1.5 text-[11px] leading-relaxed opacity-90">{meta.help}</p>

      {status && (
        <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-[10px] font-mono">
          <div>
            <div className="opacity-60 uppercase tracking-widest">Last packet</div>
            <div>
              {status.last_packet_at ? (lastPacketAgo || formatDateTime(status.last_packet_at)) : 'Never'}
            </div>
          </div>
          <div>
            <div className="opacity-60 uppercase tracking-widest">Last 24h</div>
            <div>{status.packets_24h ?? 0} packets</div>
          </div>
          <div>
            <div className="opacity-60 uppercase tracking-widest">Gateway</div>
            <div>
              {status.gateway?.status === 'healthy' ? 'Healthy' : status.gateway?.status === 'stale' ? 'Stale' : 'Offline'}
              {hbAgo ? ` · ${hbAgo}` : ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



const Stat = ({ label, value, sub, tone, icon: Icon }) => {
  const subColour =
    tone === 'warn' ? 'text-[#FF4F00]' : tone === 'ok' ? 'text-emerald-600' : 'text-muted-foreground';
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-[0.11em] text-muted-foreground mb-1 flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {label}
      </div>
      <div className="font-mono text-[13.5px] text-foreground break-all">{value}</div>
      {sub && <div className={`text-[10px] mt-0.5 font-mono ${subColour}`}>{sub}</div>}
    </div>
  );
};

export default TelemetryCard;
